import React, { memo, Suspense, useMemo } from 'react';
import { Provider } from 'react-redux';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { RootState } from 'typesafe-actions';

import { history } from 'services/history';

import AnalyticsProvider from 'blocks/AnalyticsProvider/AnalyticsProvider';
import MainLayoutContainer from 'blocks/MainLayout/MainLayout.container';
import SocketProvider from 'blocks/SocketProvider/Socket.provider';
import ThemeProvider from 'blocks/ThemeProvider';
import Web3Provider from 'blocks/Web3Provider/Web3.provider';

import store from '../store';
import FeautureRoute from './FeautureRoute.component';
import { routes, subroute } from './routes.constants';

const renderNonAuthenticated = (View: React.LazyExoticComponent<React.ComponentType>) => () => {
  window._authProtected = false;
  return <View />;
};

const renderAuthenticated = (View: React.LazyExoticComponent<React.ComponentType>, routeToRedirect?: string) => () => {
  window._authProtected = true;
  const { isAuthenticated } = (store.getState() as RootState).common.user;
  return isAuthenticated ? <View /> : <Redirect to={routeToRedirect ?? routes.getRoot()} />;
};

const HomeContainer = React.lazy(() => import(/* webpackChunkName: "HomeContainer" */ 'views/home/Home.container'));

const SpotDashboard = React.lazy(
  () => import(/* webpackChunkName: "SpotDashboard" */ 'views/spot/dashboard/SpotDashboard.container'),
);
const SearchContainer = React.lazy(
  () => import(/* webpackChunkName: "SearchContainer" */ 'views/search/Search.container'),
);
const Transactions = React.lazy(
  () => import(/* webpackChunkName: "Transactions" */ 'views/transactions/Transactions.container'),
);

const WalletContainerNew = React.lazy(
  () => import(/* webpackChunkName: "WalletContainerNew" */ '../views/wallet-new/Wallet.container'),
);

const DerivativesAsset = React.lazy(
  () => import(/* webpackChunkName: "Derivatives" */ 'views/derivatives/dashboard/DerivativesDashboard.container'),
);

const Coin = React.lazy(() => import(/* webpackChunkName: "Coin" */ 'views/coin/Coin.container'));

const ExchangeForm = React.lazy(
  () => import(/* webpackChunkName: "ExchangeForm" */ 'views/exchange/ExchangeForm/ExchangeForm.container'),
);

const Statement = React.lazy(() => import(/* webpackChunkName: "Statement" */ 'views/statement/Statement.container'));

const ExchangeChooseCurrency = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExchangeChooseCurrency" */ 'views/exchange/ExchangeChooseCurrency/ExchangeChooseCurrency.container'
    ),
);

const ExchangeHistory = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExchangeHistory" */ 'views/exchange/ExchangeForm/ExchangeHistory/ExchangeHistory.container'
    ),
);

const Deposit = React.lazy(() => import(/* webpackChunkName: "Deposit" */ 'views/frames/deposit/Deposit.component'));

const Withdrawal = React.lazy(
  () => import(/* webpackChunkName: "Withdrawal" */ 'views/frames/withdrawal/Withdrawal.component'),
);

const Menu = React.lazy(() => import(/* webpackChunkName: "Menu" */ 'views/menu/Menu.container'));
const WireTransfer = React.lazy(
  () => import(/* webpackChunkName: "WireTransfer" */ 'views/frames/wire-transfer/WireTransfer.component'),
);
const Rewards = React.lazy(() => import(/* webpackChunkName: "Rewards" */ 'views/frames/rewards/Rewards.component'));
const Referal = React.lazy(() => import(/* webpackChunkName: "Referal" */ 'views/frames/referal/Referal.component'));
const Loyalty = React.lazy(() => import(/* webpackChunkName: "Loyalty" */ 'views/frames/loyalty/Loyalty.component'));
const Receive = React.lazy(() => import(/* webpackChunkName: "Receive" */ 'views/frames/receive/Receive.component'));
const Send = React.lazy(() => import(/* webpackChunkName: "Send" */ 'views/frames/send/Send.component'));
const ChangePassword = React.lazy(
  () => import(/* webpackChunkName: "ChangePassword" */ 'views/frames/change-password/ChangePassword.component'),
);
const Security = React.lazy(
  () => import(/* webpackChunkName: "Security" */ 'views/frames/security/Security.component'),
);
const WithdrawalAddress = React.lazy(
  () =>
    import(/* webpackChunkName: "WithdrawalAddress" */ 'views/frames/withdrawal-address/WithdrawalAddress.container'),
);
const VerificationLevel = React.lazy(
  () =>
    import(/* webpackChunkName: "VerificationLevel" */ 'views/frames/verification-level/VerificationLevel.component'),
);
const CryptoPay = React.lazy(
  () => import(/* webpackChunkName: "CryptoPay" */ 'views/frames/crypto-pay/CryptoPay.component'),
);
const EditLimitOrder = React.lazy(
  () => import(/* webpackChunkName: "EditLimitOrder" */ 'views/edit-limit-order/EditLimitOrder.container'),
);
const TransferContainer = React.lazy(
  () => import(/* webpackChunkName: "TransferContainer" */ 'views/transfer/Transfer.container'),
);
const TransferChooseCurrencyContainer = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TransferChooseCurrencyContainer" */ 'views/transfer/components/TransferCurrencies/TransferCurrencies.component'
    ),
);

const EarnListPage = React.lazy(
  () => import(/* webpackChunkName: "EarnListPage" */ 'views/earn/list/EarnList.container'),
);
const CreateEarnPage = React.lazy(
  () => import(/* webpackChunkName: "CreateEarnPage" */ 'views/earn/create/CreateEarn.container'),
);

const TradeContainer = React.lazy(
  () => import(/* webpackChunkName: "TradeContainer" */ 'views/trade/Trade.page.container'),
);

const StocksDashboard = React.lazy(
  () => import(/* webpackChunkName: "StocksDashboard" */ 'views/stocks/dashboard/StocksDashboard.component'),
);

const TokenPresalePage = React.lazy(
  () => import(/* webpackChunkName: "TokenPresalePage" */ 'views/frames/token-presale/TokenPresale.container'),
);
const PaymentCardPage = React.lazy(
  () => import(/* webpackChunkName: "PaymentCardPage" */ 'views/frames/payment-card/PaymentCard.container'),
);

export const RouteComponent: React.FC<React.PropsWithChildren> = memo(() => {
  const homePath = useMemo(() => routes.getHome(), []);
  const spotAssetPagePath = useMemo(() => routes.getSpotAssetPage(), []);
  const assetSearchPagePath = useMemo(() => routes.getAssetSearchPage(), []);
  const walletPagePath = useMemo(() => routes.getWalletPage(), []);
  const exchangeFormPagePath = useMemo(() => routes.getExchangeFormPage(), []);
  const buyCryptoFormPagePath = useMemo(() => routes.getBuyCryptoFormPage(), []);
  const exchangeChooseCurrencyPagePath = useMemo(() => routes.getExchangeChooseCurrencyPage(), []);
  const transferPagePath = useMemo(() => routes.getTransferPage(), []);
  const transferChooseCurrencyPagePath = useMemo(() => routes.getTransferChooseCurrencyPage(), []);
  const statementPagePath = useMemo(() => routes.getStatementPage(), []);
  const exchangeHistoryPath = useMemo(() => routes.getExchangeHistory(), []);
  const transactionsPagePath = useMemo(() => routes.getTransactionsPage(), []);
  const spotPagePath = useMemo(() => [routes.getSpotPage(), routes.getSpotPageLegacy()], []);
  const stocksPagePath = useMemo(() => [routes.getStocksPage()], []);
  const stocksDashboardPagePath = useMemo(() => [routes.getStocksDashboardPage()], []);
  const coinPagePath = useMemo(() => routes.getCoinPage(), []);
  const depositPagePath = useMemo(() => routes.getDepositPage(), []);
  const withdrawalPagePath = useMemo(() => routes.getWithdrawalPage(), []);
  const menuPagePath = useMemo(() => routes.getMenuPage(), []);
  const wireTransferPagePath = useMemo(() => routes.getWireTransferPage(), []);
  const referalPagePath = useMemo(() => routes.getReferalPage(), []);
  const loyaltyPagePath = useMemo(() => routes.getLoyaltyPage(), []);
  const receivePagePath = useMemo(() => routes.getReceivePage(), []);
  const sendPagePath = useMemo(() => routes.getSendPage(), []);
  const securityPagePath = useMemo(() => routes.getSecurityPage(), []);
  const changePasswordPagePath = useMemo(() => routes.getChangePasswordPage(), []);
  const rewardsPagePath = useMemo(() => routes.getRewardsPage(), []);
  const withdrawalAddressPagePath = useMemo(() => routes.getWithdrawalAddressPage(), []);
  const verificationLevelPagePath = useMemo(() => routes.getVerificationLevelPage(), []);
  const cryptoPayPagePath = useMemo(() => routes.getCryptoPayPage(), []);
  const editLimitOrderPagePath = useMemo(() => routes.getEditLimitOrderPage(), []);
  const createEarnPagePath = useMemo(() => routes.getCreateEarnPage(), []);
  const earnListPagePath = useMemo(() => routes.getEarnListPage(), []);
  const derivativesPath = useMemo(() => [routes.getDerivativesPage(), routes.getDerivativesEditPositionFromGrid()], []);
  const derivativesAssetAndEditPagePath = useMemo(
    () => [routes.getDerivativesAssetPage(), routes.getDerivativesEditPosition()],
    [],
  );
  const tokenPresalePagePath = useMemo(() => routes.getTokenPresalePage(), []);
  const paymentCardPagePath = useMemo(() => routes.getPaymentCardPage(), []);

  const renderNonAuthenticatedHomeContainer = useMemo(() => renderNonAuthenticated(HomeContainer), []);
  const renderNonAuthenticatedSpotDashboard = useMemo(() => renderNonAuthenticated(SpotDashboard), []);
  const renderNonAuthenticatedSearchContainer = useMemo(() => renderNonAuthenticated(SearchContainer), []);
  const renderAuthenticatedWalletContainerNew = useMemo(() => renderAuthenticated(WalletContainerNew), []);
  const renderNonAuthenticatedExchangeForm = useMemo(() => renderNonAuthenticated(ExchangeForm), []);
  const renderNonAuthenticatedExchangeChooseCurrency = useMemo(
    () => renderNonAuthenticated(ExchangeChooseCurrency),
    [],
  );
  const renderAuthenticatedTransferContainer = useMemo(() => renderAuthenticated(TransferContainer), []);
  const renderAuthenticatedTransferChooseCurrencyContainer = useMemo(
    () => renderAuthenticated(TransferChooseCurrencyContainer),
    [],
  );
  const renderAuthenticatedStatement = useMemo(() => renderAuthenticated(Statement), []);
  const renderNonAuthenticatedExchangeHistory = useMemo(() => renderNonAuthenticated(ExchangeHistory), []);
  const renderAuthenticatedTransactions = useMemo(() => renderAuthenticated(Transactions), []);
  const renderNonAuthenticatedDerivatives = useMemo(() => renderNonAuthenticated(TradeContainer), []);
  const renderNonAuthenticatedDerivativesAsset = useMemo(() => renderNonAuthenticated(DerivativesAsset), []);
  const renderNonAuthenticatedSpot = useMemo(() => renderNonAuthenticated(TradeContainer), []);
  const renderNonAuthenticatedStocks = useMemo(() => renderNonAuthenticated(TradeContainer), []);
  const renderNonAuthenticatedStocksDashboard = useMemo(() => renderNonAuthenticated(StocksDashboard), []);
  const renderNonAuthenticatedCoin = useMemo(() => renderNonAuthenticated(Coin), []);
  const renderAuthenticatedDeposit = useMemo(() => renderAuthenticated(Deposit), []);
  const renderAuthenticatedWithdrawal = useMemo(() => renderAuthenticated(Withdrawal), []);
  const renderNonAuthenticatedMenu = useMemo(() => renderNonAuthenticated(Menu), []);
  const renderAuthenticatedWireTransfer = useMemo(() => renderAuthenticated(WireTransfer), []);
  const renderAuthenticatedReferal = useMemo(() => renderAuthenticated(Referal), []);
  const renderAuthenticatedLoyalty = useMemo(() => renderAuthenticated(Loyalty), []);
  const renderAuthenticatedReceive = useMemo(() => renderAuthenticated(Receive), []);
  const renderAuthenticatedSend = useMemo(() => renderAuthenticated(Send), []);
  const renderAuthenticatedSecurity = useMemo(() => renderAuthenticated(Security), []);
  const renderAuthenticatedChangePassword = useMemo(() => renderAuthenticated(ChangePassword), []);
  const renderAuthenticatedRewards = useMemo(() => renderAuthenticated(Rewards), []);
  const renderAuthenticatedWithdrawalAddress = useMemo(() => renderAuthenticated(WithdrawalAddress), []);
  const renderAuthenticatedVerificationLevel = useMemo(() => renderAuthenticated(VerificationLevel), []);
  const renderAuthenticatedCryptoPay = useMemo(() => renderAuthenticated(CryptoPay), []);
  const renderAuthenticatedEditLimitOrder = useMemo(() => renderAuthenticated(EditLimitOrder), []);
  const renderNonAuthenticatedCreateEarnPage = useMemo(() => renderNonAuthenticated(CreateEarnPage), []);
  const renderNonAuthenticatedEarnListPage = useMemo(() => renderNonAuthenticated(EarnListPage), []);
  const renderNonAuthenticatedTokenPresalePage = useMemo(() => renderNonAuthenticated(TokenPresalePage), []);
  const renderNonAuthenticatedPaymentCardPage = useMemo(() => renderNonAuthenticated(PaymentCardPage), []);

  return (
    <Provider store={store}>
      <Router history={history}>
        <Switch>
          <Redirect exact from={subroute} to={homePath} />
          {/* <Redirect exact from='/' to={homePath} />
          <Redirect exact from={assetPagePath} to={spotAssetPagePath} /> */}
          <Web3Provider>
            <AnalyticsProvider>
              <SocketProvider>
                <ThemeProvider>
                  <MainLayoutContainer>
                    <Suspense fallback={null}>
                      <Switch>
                        <Route path={homePath} exact render={renderNonAuthenticatedHomeContainer} />
                        <FeautureRoute
                          feature='spot'
                          path={spotAssetPagePath}
                          exact
                          render={renderNonAuthenticatedSpotDashboard}
                        />
                        <Route path={assetSearchPagePath} render={renderNonAuthenticatedSearchContainer} />
                        <Route path={walletPagePath} exact render={renderAuthenticatedWalletContainerNew} />
                        <FeautureRoute
                          feature='otc'
                          path={exchangeFormPagePath}
                          exact
                          render={renderNonAuthenticatedExchangeForm}
                        />
                        <Route path={buyCryptoFormPagePath} exact render={renderNonAuthenticatedExchangeForm} />
                        <Route
                          path={exchangeChooseCurrencyPagePath}
                          exact
                          render={renderNonAuthenticatedExchangeChooseCurrency}
                        />
                        <Route path={transferPagePath} exact render={renderAuthenticatedTransferContainer} />
                        <Route
                          path={transferChooseCurrencyPagePath}
                          exact
                          render={renderAuthenticatedTransferChooseCurrencyContainer}
                        />
                        <Route path={statementPagePath} render={renderAuthenticatedStatement} />
                        <Route path={exchangeHistoryPath} render={renderNonAuthenticatedExchangeHistory} />
                        <Route path={transactionsPagePath} render={renderAuthenticatedTransactions} />
                        <FeautureRoute
                          feature='derivatives'
                          fallbackRoute={routes.getSpotPage()}
                          exact
                          path={derivativesPath}
                          render={renderNonAuthenticatedDerivatives}
                        />
                        <FeautureRoute
                          feature='derivatives'
                          path={derivativesAssetAndEditPagePath}
                          exact
                          render={renderNonAuthenticatedDerivativesAsset}
                        />
                        <FeautureRoute
                          feature='stock'
                          path={stocksPagePath}
                          exact
                          render={renderNonAuthenticatedStocks}
                        />
                        <FeautureRoute
                          feature='stock'
                          path={stocksDashboardPagePath}
                          exact
                          render={renderNonAuthenticatedStocksDashboard}
                        />
                        <FeautureRoute feature='spot' path={spotPagePath} exact render={renderNonAuthenticatedSpot} />
                        <Route path={coinPagePath} render={renderNonAuthenticatedCoin} />
                        <Route path={depositPagePath} render={renderAuthenticatedDeposit} />
                        <Route path={withdrawalPagePath} render={renderAuthenticatedWithdrawal} />
                        <Route path={menuPagePath} render={renderNonAuthenticatedMenu} />
                        <Route path={wireTransferPagePath} render={renderAuthenticatedWireTransfer} />
                        <Route path={referalPagePath} render={renderAuthenticatedReferal} />
                        <Route path={loyaltyPagePath} render={renderAuthenticatedLoyalty} />
                        <Route path={receivePagePath} render={renderAuthenticatedReceive} />
                        <Route path={sendPagePath} render={renderAuthenticatedSend} />
                        <Route path={securityPagePath} render={renderAuthenticatedSecurity} />
                        <Route path={changePasswordPagePath} render={renderAuthenticatedChangePassword} />
                        <Route path={rewardsPagePath} render={renderAuthenticatedRewards} />
                        <Route path={withdrawalAddressPagePath} render={renderAuthenticatedWithdrawalAddress} />
                        <Route path={verificationLevelPagePath} render={renderAuthenticatedVerificationLevel} />
                        <FeautureRoute
                          feature='cryptoPay'
                          path={cryptoPayPagePath}
                          render={renderAuthenticatedCryptoPay}
                        />
                        <Route path={editLimitOrderPagePath} render={renderAuthenticatedEditLimitOrder} />
                        <Route path={createEarnPagePath} exact render={renderNonAuthenticatedCreateEarnPage} />
                        <FeautureRoute
                          feature='earn'
                          path={earnListPagePath}
                          render={renderNonAuthenticatedEarnListPage}
                        />
                        <FeautureRoute
                          feature='tokenPresaleEnabled'
                          path={tokenPresalePagePath}
                          exact
                          render={renderNonAuthenticatedTokenPresalePage}
                        />
                        <FeautureRoute
                          feature='paymentCardEnabled'
                          path={paymentCardPagePath}
                          exact
                          render={renderNonAuthenticatedPaymentCardPage}
                        />
                        <Redirect to={homePath} />
                        {/* <Redirect to={homePath} /> */}
                      </Switch>
                    </Suspense>
                  </MainLayoutContainer>
                </ThemeProvider>
              </SocketProvider>
            </AnalyticsProvider>
          </Web3Provider>
        </Switch>
      </Router>
    </Provider>
  );
});
